import PropTypes from 'prop-types';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { formatMetric, previewRows, WARNING_LABELS } from './clientReportModel';
import CallPerformancePreview from './CallPerformancePreview';

export default function ClientReportPreview({ snapshot }) {
  if (!snapshot) return null;
  if (snapshot.report_type === 'CALL_PERFORMANCE') return <CallPerformancePreview snapshot={snapshot} />;
  const metricColumns = [
    {
      field: 'spend',
      headerName: 'Spend',
      width: 140,
      renderCell: ({ row }) => formatMetric(row.spend, 'money', row.currency),
    },
    ...(snapshot.report_type === 'CAMPAIGN_PERFORMANCE'
      ? [
          {
            field: 'impressions',
            headerName: 'Impressions',
            width: 120,
            renderCell: ({ value }) => formatMetric(value),
          },
          { field: 'clicks', headerName: 'Clicks', width: 90, renderCell: ({ value }) => formatMetric(value) },
          {
            field: 'ctr_percent',
            headerName: 'CTR',
            width: 100,
            renderCell: ({ value }) => formatMetric(value, 'percent'),
          },
          {
            field: 'cpc',
            headerName: 'CPC',
            width: 120,
            renderCell: ({ row }) => formatMetric(row.cpc, 'cpc', row.currency),
          },
        ]
      : []),
  ];
  const columns = [
    { field: 'month', headerName: 'Month', width: 110 },
    { field: 'practice', headerName: 'Location', minWidth: 150, flex: 1 },
    { field: 'platform_label', headerName: 'Platform', width: 110 },
    { field: 'account_id', headerName: 'Account', width: 100 },
    { field: 'campaign_name', headerName: 'Campaign', minWidth: 180, flex: 1 },
    ...metricColumns,
  ];
  const rows = previewRows(snapshot);
  const months = [...new Set(rows.map((row) => row.month))];
  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">
        {snapshot.title} · {snapshot.period?.label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Timezone: {snapshot.period?.timezone}. Metrics keep each account's local calendar dates.
      </Typography>
      {!snapshot.deliverable && (
        <Alert severity="warning">
          Incomplete or unavailable data. Preview and downloads are available; email delivery is blocked.
        </Alert>
      )}
      {(snapshot.warnings || []).map((warning) => (
        <Alert severity="warning" key={JSON.stringify(warning)}>
          {snapshot.practices?.find((p) => p.id === warning.practice_id)?.name || `Location ${warning.practice_id}`} ·{' '}
          {{ google_ads: 'Google Ads', meta: 'Meta', facebook: 'Facebook' }[warning.platform] || warning.platform}
          {warning.account_id ? ` · Account ${warning.account_id}` : ''}:{' '}
          {WARNING_LABELS[warning.code] || 'Report data needs review.'}
        </Alert>
      ))}
      {(snapshot.practices || []).map((practice) => (
        <Box key={practice.id}>
          <Typography variant="subtitle2">{practice.name} — linked accounts</Typography>
          {(practice.accounts || []).map((account) => (
            <Typography variant="body2" color="text.secondary" key={`${account.platform}:${account.id}`}>
              {account.platform_label} · {account.name || account.provider_account_id} ({account.provider_account_id}) ·{' '}
              {account.currency || 'Currency unknown'} · {account.timezone || 'Timezone unknown'} ·{' '}
              {account.complete ? 'Coverage verified' : 'Coverage incomplete'}
            </Typography>
          ))}
          {(practice.totals || []).map((total) => (
            <Typography key={total.currency || 'unknown'} variant="body2">
              Location subtotal ({total.currency || 'Currency unknown'}):{' '}
              {formatMetric(total.spend, 'money', total.currency)}
            </Typography>
          ))}
        </Box>
      ))}
      {months.map((month) => (
        <Box key={month}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {new Date(`${month}-01T12:00:00Z`).toLocaleDateString('en-GB', {
              month: 'long',
              year: 'numeric',
              timeZone: 'UTC',
            })}
          </Typography>
          <Box sx={{ height: 360, width: '100%' }}>
            {/* v6 pagination API: pageSize/rowsPerPageOptions/
                disableSelectionOnClick were all removed, so the author's
                page size was silently ignored. */}
            <DataGridPro
              rows={rows.filter((row) => row.month === month)}
              columns={columns}
              disableRowSelectionOnClick
              pagination
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            />
          </Box>
        </Box>
      ))}
      {!rows.length && (
        <Typography color="text.secondary">
          No stored campaign rows for this period. Check coverage warnings before interpreting an empty report.
        </Typography>
      )}
      <Typography variant="subtitle2">Totals by currency</Typography>
      {(snapshot.totals || []).map((total) => (
        <Typography key={total.currency || 'unknown'} variant="body2">
          {total.currency || 'Currency unknown'}: {formatMetric(total.spend, 'money', total.currency)}
        </Typography>
      ))}
      <Typography variant="caption" color="text.secondary">
        Currencies are never combined. CTR and CPC use total clicks, impressions and spend.
      </Typography>
    </Stack>
  );
}
ClientReportPreview.propTypes = { snapshot: PropTypes.object };
